import React from "react";
import type { Admin } from "../AdminsPage";
import { getInitials } from "../AdminsPage";

interface AdminActionModalProps {
    isOpen: boolean;
    selectedAdmin: Admin | null;
    isUpdating: boolean;
    suspensionConfig?: any;
    suspendType: string;
    setSuspendType: (type: string) => void;
    suspendDuration: number;
    setSuspendDuration: (duration: number) => void;
    suspendReason: string;
    setSuspendReason: (reason: string) => void;
    reasonError: boolean;
    setReasonError: (err: boolean) => void;
    onClose: () => void;
    onConfirm: () => void;
}

const AdminActionModal: React.FC<AdminActionModalProps> = ({
    isOpen, selectedAdmin, isUpdating, suspensionConfig,
    suspendType, setSuspendType, suspendDuration, setSuspendDuration,
    suspendReason, setSuspendReason, reasonError, setReasonError,
    onClose, onConfirm
}) => {
    if (!isOpen || !selectedAdmin) return null;

    // Evaluamos directamente el estado para saber la acción
    const isSuspending = selectedAdmin.status === "AC";

    const activeTypeConfig = suspensionConfig?.config?.suspensionTypes?.find((t: any) => t.id === suspendType);
    const showDurationSelector = activeTypeConfig?.allowsDuration === true;

    return (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-white border border-gray-200 rounded-xl p-7 w-[420px] max-w-[95vw] shadow-xl">

                <div
                    className="w-[52px] h-[52px] rounded-xl mx-auto mb-4 flex items-center justify-center text-[26px]"
                    style={{ background: isSuspending ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)" }}
                >
                    {isSuspending ? "🚫" : "✅"}
                </div>

                <h3 className="text-[17px] font-bold text-center mb-1 text-gray-900">
                    {isSuspending ? "Deshabilitar rol de administrador" : "Habilitar rol de administrador"}
                </h3>

                <p className="text-xs text-gray-500 text-center mb-4">
                    {isSuspending
                        ? "Este usuario perderá los accesos al sistema de administración."
                        : "Se restaurarán los permisos de administrador a este usuario."}
                </p>

                <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-lg p-2.5 px-3.5 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 overflow-hidden bg-gradient-to-br from-[#4f6df5] to-[#2563eb]">
                        {selectedAdmin.photoLink ? <img src={selectedAdmin.photoLink} alt={selectedAdmin.name} className="w-full h-full object-cover" /> : getInitials(selectedAdmin.name, selectedAdmin.lastName)}
                    </div>
                    <div>
                        <p className="text-[13px] font-semibold m-0 text-gray-900">{selectedAdmin.name} {selectedAdmin.lastName}</p>
                        <span className="text-[11px] text-gray-500">{selectedAdmin.userName || "Sin correo"}</span>
                    </div>
                </div>

                {/* Esta sección solo se mostrará si le pasamos suspensionConfig (Para admins lo pasaremos como null) */}
                {isSuspending && suspensionConfig && (
                    <>
                        <div className="flex flex-col gap-1.5 mb-3">
                            {suspensionConfig.config.suspensionTypes.map((typeOpt: any) => (
                                <div key={typeOpt.id} className={`flex items-center gap-2.5 p-2.5 px-3 rounded-lg border cursor-pointer transition-all ${suspendType === typeOpt.id ? "border-red-400 bg-red-50" : "border-gray-200 bg-white hover:border-red-300"}`} onClick={() => setSuspendType(typeOpt.id)}>
                                    <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${suspendType === typeOpt.id ? "border-red-500" : "border-gray-300"}`}>
                                        {suspendType === typeOpt.id && <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>}
                                    </div>
                                    <div>
                                        <p className="text-[13px] font-semibold text-gray-900 m-0 leading-tight">{typeOpt.label}</p>
                                        <span className="text-[11px] text-gray-500">{typeOpt.description}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {showDurationSelector && (
                            <div className="flex gap-1.5 mb-3">
                                {suspensionConfig.config.durationOptions.map((durOpt: any) => (
                                    <button
                                        key={durOpt.value}
                                        className={`flex-1 py-1.5 text-center rounded-md border text-[11px] font-semibold cursor-pointer transition-all ${suspendDuration === durOpt.value ? "border-amber-400 text-amber-600 bg-amber-50" : "border-gray-200 text-gray-600 bg-white hover:border-gray-300 hover:bg-gray-50"}`}
                                        onClick={() => setSuspendDuration(durOpt.value)}
                                    >
                                        {durOpt.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="h-px bg-gray-200 my-3.5"></div>
                        <textarea rows={3} placeholder="Razón de la suspensión (requerido)..." value={suspendReason} onChange={(e) => { setSuspendReason(e.target.value); if (e.target.value.trim()) setReasonError(false); }} className={`w-full bg-white border rounded-lg p-2.5 px-3 text-gray-900 text-xs resize-none outline-none transition-colors placeholder:text-gray-400 ${reasonError ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500" : "border-gray-300 focus:border-red-400 focus:ring-1 focus:ring-red-400"}`}></textarea>
                        {reasonError && <span className="text-[10px] text-red-500 mt-1 block">Debes ingresar una razón para suspender la cuenta.</span>}
                    </>
                )}

                <div className="flex gap-2 mt-4">
                    <button className="flex-1 py-2 rounded-lg text-xs font-semibold bg-white border border-gray-300 text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors shadow-sm"
                        onClick={onClose}
                        disabled={isUpdating}>
                        Cancelar
                    </button>
                    <button className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all shadow-sm border-none text-white hover:opacity-90`}
                        style={{ background: isSuspending ? "#ef4444" : "#10b981", opacity: isUpdating ? 0.7 : 1, cursor: isUpdating ? "not-allowed" : "pointer" }}
                        onClick={onConfirm}
                        disabled={isUpdating}>
                        {isUpdating ? <>
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" strokeDasharray="30" strokeLinecap="round" />
                            </svg>
                            Procesando...</> : isSuspending ? "Confirmar" : "Habilitar"
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminActionModal;